/**
 * Self-heal for expired media URLs.
 *
 * Every attachment image/video the app renders is a GCS signed URL that lives
 * 6 hours (security decision: no public bucket). A tab left open longer than
 * that (a post overnight, the calendar on a second monitor, a PWA resumed the
 * next morning) silently turns into broken images, and "refresh the page" is
 * not an answer for a core experience.
 *
 * Rather than plumbing an attachment id through ~50 render sites, we key the
 * heal on the URL itself: the signed URL path is `/<bucket>/<owner>/<id>.<ext>`
 * (or `<id>-thumb.jpg` for video posters), so the attachment id is always
 * recoverable, and `Expires` tells us when it dies. One document-level
 * capturing `error` listener heals anything that fails to load; a focus /
 * visibility sweep and a slow interval refresh anything about to expire before
 * the user ever sees a broken tile.
 *
 * This file is the pure, node-testable core (no DOM types beyond a minimal
 * element shape). The browser wiring lives in media-url-self-heal.install.ts.
 */

export type MediaVariant = 'main' | 'thumbnail'

export interface SignedMediaRef {
  attachmentId: string
  variant: MediaVariant
  /** Epoch ms when the signature dies, or null when the URL carries no Expires. */
  expiresAt: number | null
}

export interface FreshUrl {
  url: string
  /** ISO timestamp from the server. */
  expiresAt: string
}

export type FreshUrlFetcher = (attachmentId: string, variant: MediaVariant) => Promise<FreshUrl>

export const SIGNED_MEDIA_HOST = 'storage.googleapis.com'
/** A URL this close to expiry is refreshed proactively (sweeps), not just on error. */
export const EXPIRY_MARGIN_MS = 15 * 60 * 1000
/** The dataset key that records which src an element was last healed FROM (loop guard). */
export const HEALED_FROM_ATTR = 'data-media-healed-from'

const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/

/**
 * Recognize one of OUR signed media URLs and pull the attachment id, variant and
 * expiry out of it. Anything else (CDN logos, data URIs, blob previews, foreign
 * buckets) returns null and is left alone.
 */
export function parseSignedMediaUrl(url: string | null | undefined): SignedMediaRef | null {
  if (!url || typeof url !== 'string') return null
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return null
  }
  if (parsed.hostname !== SIGNED_MEDIA_HOST) return null
  const segments = parsed.pathname.split('/').filter(Boolean)
  // /<bucket>/<owner>/<file>
  if (segments.length !== 3) return null
  const file = decodeURIComponent(segments[2])
  const dot = file.lastIndexOf('.')
  const base = dot > 0 ? file.slice(0, dot) : file
  let attachmentId = base
  let variant: MediaVariant = 'main'
  if (base.endsWith('-thumb')) {
    attachmentId = base.slice(0, -'-thumb'.length)
    variant = 'thumbnail'
  }
  if (!ULID_RE.test(attachmentId)) return null
  const expiresParam = parsed.searchParams.get('Expires')
  const expiresSec = expiresParam ? Number(expiresParam) : NaN
  const expiresAt = Number.isFinite(expiresSec) && expiresSec > 0 ? expiresSec * 1000 : null
  return { attachmentId, variant, expiresAt }
}

/** True when the URL is expired or will expire within the margin. Unknown expiry = false. */
export function isExpiringSoon(
  ref: SignedMediaRef | null,
  now: number,
  marginMs = EXPIRY_MARGIN_MS
): boolean {
  if (!ref || ref.expiresAt === null) return false
  return ref.expiresAt - now <= marginMs
}

/** Minimal element surface the healer touches, so it is testable without a DOM. */
export interface MediaElementLike {
  tagName: string
  getAttribute(name: string): string | null
  setAttribute(name: string, value: string): void
  /** <video>/<audio>: re-run resource selection after a src/<source> swap. */
  load?: () => void
  /** <video>: nested <source> children. */
  querySelectorAll?: (selector: string) => ArrayLike<MediaElementLike>
  /** <source>: its owning media element. */
  parentElement?: MediaElementLike | null
}

export interface HealEvent {
  attachmentId: string
  variant: MediaVariant
  reason: 'error' | 'sweep'
  /** Was the old URL already past its Expires (vs. a transient failure / proactive refresh)? */
  wasExpired: boolean
}

export interface HealerDeps {
  fetchFresh: FreshUrlFetcher
  now?: () => number
  onHealed?: (event: HealEvent) => void
  onFailed?: (attachmentId: string, error: unknown) => void
}

/** Which attributes on which tags may carry a signed media URL. */
const URL_ATTRS: Record<string, string[]> = {
  IMG: ['src'],
  SOURCE: ['src'],
  AUDIO: ['src'],
  VIDEO: ['src', 'poster'],
}

export function createMediaUrlHealer(deps: HealerDeps) {
  const now = deps.now ?? (() => Date.now())
  const fresh = new Map<string, { url: string; expiresAt: number }>()
  const inflight = new Map<string, Promise<string | null>>()
  const key = (ref: SignedMediaRef) => `${ref.attachmentId}:${ref.variant}`

  /**
   * A fresh URL for the attachment behind `url`, or null when `url` is not ours
   * or the server refused (deleted attachment, no access). Deduped per
   * attachment so a grid of 30 tiles for one photo costs one request.
   */
  async function refreshUrl(url: string, opts?: { force?: boolean }): Promise<string | null> {
    const ref = parseSignedMediaUrl(url)
    if (!ref) return null
    const k = key(ref)
    // Serve from cache unless the caller forces it, the cached URL is itself the
    // one that just failed (deleted object / revoked), or it is about to expire.
    const cached = fresh.get(k)
    if (
      cached &&
      !opts?.force &&
      cached.url !== url &&
      cached.expiresAt - now() > EXPIRY_MARGIN_MS
    ) {
      return cached.url
    }
    const pending = inflight.get(k)
    if (pending) return pending
    const p = deps
      .fetchFresh(ref.attachmentId, ref.variant)
      .then(res => {
        const expiresAt = Date.parse(res.expiresAt)
        fresh.set(k, {
          url: res.url,
          expiresAt: Number.isFinite(expiresAt) ? expiresAt : now() + 5 * 60 * 1000,
        })
        return res.url
      })
      .catch((err: unknown) => {
        deps.onFailed?.(ref.attachmentId, err)
        return null
      })
      .finally(() => inflight.delete(k))
    inflight.set(k, p)
    return p
  }

  /**
   * Swap every signed-URL attribute on the element for a fresh one. Returns true
   * when at least one attribute changed. Loop guard: an element is healed at most
   * once per distinct source URL (recorded in HEALED_FROM_ATTR), so a URL that
   * keeps failing after a fresh signature (deleted object) does not spin.
   */
  async function healElement(el: MediaElementLike, reason: HealEvent['reason']): Promise<boolean> {
    const target = el.tagName === 'SOURCE' && el.parentElement ? el.parentElement : el
    const attrs = URL_ATTRS[target.tagName] ?? []
    const candidates: Array<{ node: MediaElementLike; attr: string }> = attrs.map(attr => ({
      node: target,
      attr,
    }))
    if (target.tagName === 'VIDEO' && target.querySelectorAll) {
      const sources = target.querySelectorAll('source')
      for (let i = 0; i < sources.length; i++) candidates.push({ node: sources[i], attr: 'src' })
    }

    let changed = false
    const healedFrom = target.getAttribute(HEALED_FROM_ATTR)
    for (const { node, attr } of candidates) {
      const current = node.getAttribute(attr)
      const ref = parseSignedMediaUrl(current)
      if (!current || !ref) continue
      // On error: only ours, and never the same failing URL twice. On sweep: only when expiring.
      if (reason === 'error' && healedFrom === current) continue
      if (reason === 'sweep' && !isExpiringSoon(ref, now())) continue
      const wasExpired = ref.expiresAt !== null && ref.expiresAt <= now()
      const next = await refreshUrl(current)
      if (!next || next === current) continue
      node.setAttribute(attr, next)
      target.setAttribute(HEALED_FROM_ATTR, current)
      changed = true
      deps.onHealed?.({ attachmentId: ref.attachmentId, variant: ref.variant, reason, wasExpired })
    }
    if (changed && target.load) target.load()
    return changed
  }

  /** Proactively refresh everything under `root` that is expired or about to expire. */
  async function sweep(root: {
    querySelectorAll(selector: string): ArrayLike<MediaElementLike>
  }): Promise<number> {
    const nodes = root.querySelectorAll(
      'img[src], video[src], video[poster], audio[src], source[src]'
    )
    let healed = 0
    const seen = new Set<MediaElementLike>()
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i]
      const target = node.tagName === 'SOURCE' && node.parentElement ? node.parentElement : node
      if (seen.has(target)) continue
      seen.add(target)
      if (await healElement(target, 'sweep')) healed++
    }
    return healed
  }

  return { refreshUrl, healElement, sweep }
}

export type MediaUrlHealer = ReturnType<typeof createMediaUrlHealer>
